import React, { useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { createBreakpoint, useClickAway } from 'react-use';
import { IFulfillment } from '../../lib/ssp/types';
import { getOrderFlowPath, OrderFlow } from '../../lib/ssp/utils/routesUtil';
import { useInterfaceSettings } from '../../providers/InterfaceSettings/useInterfaceSettings';
import { BREAKPOINTS, BREAKPOINT_IDS } from '../../styles/responsive';
import { StyledCollapse } from '../Order/styled';
import Price from '../Price';
import FulfillmentStatus from '../self-serve/FulfillmentStatus';

import { hiddenStatuses } from './constants';
import ItemQuantityWithRefundedQuantity from './ItemQuantityWithRefundedQuantity';
import {
  Container,
  Flows,
  Header,
  ItemDetails,
  ItemPriceAndQuantity,
  ItemTitle,
  LineItem,
  MoreMenu,
  StyledInsertPhotoSvg,
  StyledKebabMenuSvg,
  StyledLink,
} from './styled';
import ToggleItems from './ToggleItems';

const breakpoints: Record<string, number> = {};
BREAKPOINTS.forEach((breakpoint) => {
  breakpoints[breakpoint.id] = breakpoint.width;
});
const useBreakpoint = createBreakpoint(breakpoints);

export type FulfillmentComponentProps = {
  fulfillment: IFulfillment;
  isLast: boolean;
  orderId: number;
};

const Fulfillment = ({
  fulfillment,
  isLast,
  orderId,
}: FulfillmentComponentProps) => {
  const {
    id: fulfillmentId,
    gorgias_order_status: status,
    flows,
    line_items: lineItems,
  } = fulfillment;
  const firstLineItem = lineItems[0];
  const { t } = useTranslation();
  const uiSettings = useInterfaceSettings();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isKebabMenuOpen, setIsKebabMenuOpen] = useState(false);
  const breakpoint = useBreakpoint();
  const moreButtonRef = useRef<HTMLDivElement>(null);

  const isReportIssueButtonAvailable =
    Array.isArray(flows.report_issue_reasons) &&
    flows.report_issue_reasons.length > 0;

  useClickAway(moreButtonRef, () => {
    setIsKebabMenuOpen(false);
  });

  const shouldShowKebabMenu = () => {
    return (
      isReportIssueButtonAvailable &&
      breakpoint === BREAKPOINT_IDS.smallMobile &&
      [flows.track_order, flows.cancel_order, flows.return_order].filter(
        (key) => key,
      ).length >= 2
    );
  };

  const getReportIssueButton = (isMoreItem = false) =>
    isReportIssueButtonAvailable && (
      <StyledLink
        isMoreItem={isMoreItem}
        href={getOrderFlowPath(OrderFlow.report, { orderId, fulfillmentId })}
      >
        {t('orderFlowReport')}
      </StyledLink>
    );

  let reportIssueButton = getReportIssueButton(false);
  if (shouldShowKebabMenu()) {
    reportIssueButton = (
      <MoreMenu ref={moreButtonRef}>
        <StyledKebabMenuSvg
          onClick={() => setIsKebabMenuOpen(!isKebabMenuOpen)}
        />
        {isKebabMenuOpen && getReportIssueButton(true)}
      </MoreMenu>
    );
  }

  return (
    <Container isLast={isLast} className="fulfillment">
      <Header>
        <Flows>
          {flows.track_order && (
            <StyledLink
              href={getOrderFlowPath(OrderFlow.track, {
                orderId,
                fulfillmentId,
              })}
            >
              {t('orderFlowTrack')}
            </StyledLink>
          )}
          {flows.return_order && (
            <StyledLink
              href={getOrderFlowPath(OrderFlow.return, {
                orderId,
                fulfillmentId,
              })}
            >
              {t('orderFlowReturn')}
            </StyledLink>
          )}
          {flows.cancel_order && (
            <StyledLink
              href={getOrderFlowPath(OrderFlow.cancel, {
                orderId,
                fulfillmentId,
              })}
            >
              {t('orderFlowCancel')}
            </StyledLink>
          )}
          {reportIssueButton}
        </Flows>
        {!hiddenStatuses.includes(status) && (
          <FulfillmentStatus status={status} />
        )}
      </Header>
      <LineItem key={`lineItem-${firstLineItem.id}`}>
        {firstLineItem.product_image_url !== null ? (
          <Image
            src={firstLineItem.product_image_url}
            alt={firstLineItem.name}
            width={56}
            height={56}
          />
        ) : (
          <StyledInsertPhotoSvg />
        )}

        <ItemDetails>
          <ItemTitle>{firstLineItem.name}</ItemTitle>
          <ItemPriceAndQuantity>
            <Price
              size="small"
              value={firstLineItem.price_set.presentment_money.amount}
              currency={firstLineItem.price_set.presentment_money.currency_code}
              locale={uiSettings.activeLocale}
            />
            <ItemQuantityWithRefundedQuantity
              quantity={firstLineItem.quantity}
              refundedQuantity={firstLineItem.refunded_quantity}
            />
          </ItemPriceAndQuantity>
        </ItemDetails>
      </LineItem>
      <StyledCollapse isOpened={isExpanded}>
        {lineItems.map((lineItem, index) => {
          const {
            product_image_url: imageUrl,
            id,
            name,
            price_set: {
              presentment_money: { amount, currency_code: currency },
            },
            quantity,
            refunded_quantity,
          } = lineItem;

          return (
            index !== 0 && (
              <LineItem key={`lineItem-${id}`}>
                {imageUrl !== null ? (
                  <Image
                    src={imageUrl}
                    alt={lineItem.name}
                    width={56}
                    height={56}
                  />
                ) : (
                  <StyledInsertPhotoSvg />
                )}

                <ItemDetails>
                  <ItemTitle>{name}</ItemTitle>
                  <ItemPriceAndQuantity>
                    <Price
                      size="small"
                      value={amount}
                      currency={currency}
                      locale={uiSettings.activeLocale}
                    />
                    <ItemQuantityWithRefundedQuantity
                      quantity={quantity}
                      refundedQuantity={refunded_quantity}
                    />
                  </ItemPriceAndQuantity>
                </ItemDetails>
              </LineItem>
            )
          );
        })}
      </StyledCollapse>
      {lineItems.length > 1 && (
        <ToggleItems
          isExpanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
          itemsMore={lineItems.length - 1}
        />
      )}
    </Container>
  );
};

export default Fulfillment;
